import { createFitnessId } from '@/lib/fitnessId';
import { isGuidedPreparationBlocking, readFitnessGuidedPreparation } from '@/lib/fitnessGuidedPreparation';
import { checkpointWorkoutDraft, retireWorkoutDraftCheckpoint } from "@/lib/fitnessDraftPersistence";
import { workoutDraftKey, workoutDraftRetirementClientId } from "@/lib/fitnessDraftKeys";
import { notifyFitnessDraftWrite } from "@/lib/fitnessDraftWriter";
import { validFitnessExerciseMetadata, validFitnessSetMetadata } from '../../supabase/functions/_shared/fitnessWorkoutMetadata';

export { workoutDraftKey };
export type WorkoutDraft = Record<string, any> & { version: 1; userId: string; exercises: any[] };

export function getWorkoutDraft(userId: string): WorkoutDraft | null {
  let draft: WorkoutDraft | null;
  try { const raw = sessionStorage.getItem(workoutDraftKey(userId)); draft = raw ? JSON.parse(raw) as WorkoutDraft : null; } catch { return null; }
  if (!draft || draft.version !== 1 || draft.userId !== userId || !Array.isArray(draft.exercises) || !draft.exercises.length) return null;
  const nonnegative = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0;
  const positiveInteger = (v: unknown) => nonnegative(v) && Number.isInteger(v) && Number(v) > 0;
  if (!draft.exercises.every(ex => ex && typeof ex.name === "string" && ex.name.trim() && typeof ex.muscle === "string" &&
    validFitnessExerciseMetadata(ex) && nonnegative(ex.defaultWeight) && positiveInteger(ex.defaultReps) &&
    Array.isArray(ex.sets) && ex.sets.length > 0 && ex.sets.every((set: any) => set && nonnegative(set.weight) && positiveInteger(set.reps) && typeof set.done === "boolean" && validFitnessSetMetadata(set)))) return null;
  if (!Number.isFinite(Date.parse(draft.startedAt)) || !["setup", "countdown", "execution", "rest", "exercise-complete"].includes(draft.phase)) return null;
  const index = draft.currentExerciseIndex;
  if (!Number.isInteger(index) || index < 0 || index >= draft.exercises.length || !positiveInteger(draft.currentSet) || draft.currentSet > draft.exercises[index].sets.length) return null;
  if (!draft.exerciseConfigs || !draft.exercises.every((_ex, i) => {
    const cfg = draft.exerciseConfigs[i];
    return cfg && nonnegative(cfg.weight) && positiveInteger(cfg.reps) && positiveInteger(cfg.sets) && cfg.sets === draft.exercises[i].sets.length;
  })) return null;
  if (draft.editingWeight != null && !nonnegative(draft.editingWeight)) return null;
  if (draft.editingReps != null && !nonnegative(draft.editingReps)) return null;
  if (draft.restDuration != null && !nonnegative(draft.restDuration)) return null;
  if (draft.restDeadline != null && !nonnegative(draft.restDeadline)) return null;
  if (draft.actualSetValues != null && (typeof draft.actualSetValues !== "object" || !Object.values(draft.actualSetValues).every(values => Array.isArray(values) && values.every(v => v && nonnegative(v.weight) && positiveInteger(v.reps))))) return null;
  if (draft.guidedPreparation !== undefined) {
    try { draft.guidedPreparation = readFitnessGuidedPreparation(draft.guidedPreparation); } catch { return null; }
    if (isGuidedPreparationBlocking(draft.guidedPreparation) && (draft.phase !== 'setup' || index !== 0 || draft.currentSet !== 1 || draft.exercises.some(ex => ex.sets.some((set: { done: boolean }) => set.done)))) return null;
  }
  return draft;
}

export function saveWorkoutDraft(userId: string, draft: Record<string, unknown>): void {
  notifyFitnessDraftWrite(userId);
  checkpointWorkoutDraft(userId, JSON.stringify({ ...draft, version: 1, userId }));
}

export async function removeWorkoutDraft(userId: string, isCurrent: () => boolean = () => true): Promise<void> {
  const key = workoutDraftKey(userId), raw = sessionStorage.getItem(key);
  if (raw === null) return;
  const clientId = workoutDraftRetirementClientId(raw, createFitnessId());
  await retireWorkoutDraftCheckpoint(userId, clientId, isCurrent);
  if (!isCurrent() || sessionStorage.getItem(key) !== raw) throw new Error("fitness_draft_changed");
  sessionStorage.removeItem(key);
  if (sessionStorage.getItem(key) !== null) throw new Error("fitness_draft_session_unconfirmed");
  notifyFitnessDraftWrite(userId);
}
